import React, { useState, useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { TextField, TextAreaField, CheckboxGroupField } from "./ui/FormControls";
import { ImageDropField } from "./ui/ImageDropField";
import { fetchServicesBySlug } from "../store/slices/businessOwnerSlice";
import { Plus } from "lucide-react";

// Validation schemas
const businessDetailsSchema = yup.object({
  business_name: yup.string().max(255, "Business name must be less than 255 characters").nullable(),
  about_business: yup.string().nullable(),
  address: yup.string().nullable(),
  whatsapp_number: yup.string().max(15, "WhatsApp number must be max 15 digits").nullable(),
  website: yup.string().nullable().url("Enter a valid URL"),
  email: yup.string().nullable().email("Enter a valid email"),
  business_location_map: yup.string().nullable(),
  latitude: yup.number().transform((value, originalValue) => {
    return originalValue === '' ? null : value;
  }).min(-90).max(90).nullable(),
  longitude: yup.number().transform((value, originalValue) => {
    return originalValue === '' ? null : value;
  }).min(-180).max(180).nullable(),
  pincode: yup.string().max(6, "Pincode must be max 6 digits").nullable(),
  contact_numbers: yup.array().of(yup.string()).nullable(),
  payment_modes: yup.array().of(yup.string()).nullable(),
  business_timings: yup.object().nullable(),
  services: yup.array().of(yup.string()).nullable(),
});

const discountSchema = yup.object({
  discount_text: yup.string().max(255, "Discount text must be less than 255 characters").nullable(),
  fixed_discount: yup.number().min(0, "Must be 0 or more").nullable(),
  customer_discount: yup.number().min(0, "Must be 0 or more").nullable(),
  company_commission: yup.number().min(0, "Must be 0 or more").nullable(),
});

const gallerySchema = yup.object({
  gallery: yup.mixed().nullable(),
  remove_images: yup.array().of(yup.string()).nullable(),
});

// Custom Services Checkbox Component
const ServicesCheckboxGroup = ({ 
  label, 
  value = [], 
  onChange, 
  options = [], 
  loading = false, 
  error, 
  hint
}) => {
  const [showAddNew, setShowAddNew] = useState(false);
  const [newServiceName, setNewServiceName] = useState("");

  // Handle service selection
  const handleServiceToggle = (serviceValue) => {
    const newValue = value.includes(serviceValue)
      ? value.filter(v => v !== serviceValue)
      : [...value, serviceValue];
    onChange(newValue);
  };

  // Handle adding new service
  const handleAddNewService = () => {
    const trimmedName = newServiceName.trim();
    if (trimmedName && !value.includes(trimmedName)) {
      // Check if it matches any existing service name (case insensitive)
      const existingService = options.find(opt => 
        opt.label.toLowerCase() === trimmedName.toLowerCase()
      );
      
      if (existingService) {
        // If it matches an existing service, use the existing service's name
        onChange([...value, existingService.value]);
      } else {
        // If it's truly new, add it as a custom service
        onChange([...value, trimmedName]);
      }
      
      setNewServiceName("");
      setShowAddNew(false);
    }
  };

  // Get custom services (services that are selected but not in options)
  const customServices = value.filter(val => 
    !options.some(opt => opt.value === val)
  );

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {label}
        </label>
      )}
      
      {loading ? (
        <div className="text-center py-4 text-gray-500">Loading services...</div>
      ) : (
        <div className="space-y-4">
          {/* Add New Service - Moved to top */}
          <div className="pb-4 border-b border-gray-200">
            {!showAddNew ? (
              <button
                type="button"
                onClick={() => setShowAddNew(true)}
                className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Custom Service
              </button>
            ) : (
              <div className="space-y-3">
                <div className="text-xs text-gray-500 mb-2">
                  Services for hospitality-lodging category. Check existing services or add custom ones.
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={newServiceName}
                    onChange={(e) => setNewServiceName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddNewService();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setShowAddNew(false);
                        setNewServiceName("");
                      }
                    }}
                    placeholder="Enter service name"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAddNewService}
                      disabled={!newServiceName.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddNew(false);
                        setNewServiceName("");
                      }}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Existing Services */}
          {options.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-gray-600 mb-3">Available Services</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {options.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={value.includes(option.value)}
                      onChange={() => handleServiceToggle(option.value)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-sm text-gray-700 flex-1 leading-tight">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Custom Services */}
          {customServices.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-gray-600 mb-3">Custom Services</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {customServices.map((service) => (
                  <label
                    key={service}
                    className="flex items-center space-x-3 p-3 border border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={() => handleServiceToggle(service)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-sm text-blue-700 flex-1 leading-tight">{service}</span>
                    <span className="text-xs text-blue-500 font-medium">(Custom)</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* No services message */}
          {options.length === 0 && customServices.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No services available for this category.</p>
              <p className="text-xs mt-1">You can add custom services using the button above.</p>
            </div>
          )}
        </div>
      )}

      {/* Error and Hint */}
      {error ? (
        <p className="text-red-500 text-sm mt-2">{error}</p>
      ) : hint ? (
        <p className="text-gray-500 text-xs mt-2">{hint}</p>
      ) : null}
    </div>
  );
};

// Business Details Edit Form
export const BusinessDetailsEditForm = ({ data, onSave, loading = false, paymentModes = [] }) => {
  const dispatch = useDispatch();
  const { services, servicesLoading } = useSelector((state) => state.businessOwner);
  const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  // Get category slug from business data
  const [categorySlug, setCategorySlug] = useState(null);
  
  // Initialize business timings with 2 slots per day
  const initializeTimings = (businessData) => {
    const existingTimings = businessData?.business_timings || {};
    const timings = {};
    
    allDays.forEach(day => {
      const daySlots = existingTimings[day] || [];
      timings[day] = [
        daySlots[0] || { open: '', close: '' },
        daySlots[1] || { open: '', close: '' }
      ];
    });
    
    return timings;
  };

  // Filter payment modes to only include valid ones from the API
  const filterValidPaymentModes = (modes) => {
    if (!Array.isArray(modes)) return [];
    const validModeNames = paymentModes.map(pm => pm.name);
    return modes.filter(mode => validModeNames.includes(mode));
  };

  // Extract selected service names from business data
  const getSelectedServices = (businessData) => {
    if (!businessData?.services || !Array.isArray(businessData.services)) return [];
    return businessData.services.map(service => service.name);
  };

  // Fetch services when component mounts
  useEffect(() => {
    // Get category slug from business data
    const slug = data?.category?.slug;
    
    if (slug) {
      setCategorySlug(slug);
      dispatch(fetchServicesBySlug(slug));
    } else {
      console.log('No category slug found in business data:', data);
    }
  }, [dispatch, data]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(businessDetailsSchema),
    defaultValues: {
      business_name: data?.business_name || "",
      about_business: data?.about_business || "",
      address: data?.address || "",
      whatsapp_number: data?.whatsapp_number || "",
      website: data?.website || "",
      email: data?.email || "",
      business_location_map: data?.business_location_map || "",
      latitude: data?.latitude || null,
      longitude: data?.longitude || null,
      pincode: data?.pincode?.toString() || "",
      contact_numbers: Array.isArray(data?.contact_numbers) ? data.contact_numbers : [""],
      payment_modes: filterValidPaymentModes(data?.payment_modes),
      business_timings: initializeTimings(data),
      services: getSelectedServices(data),
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        business_name: data?.business_name || "",
        about_business: data?.about_business || "",
        address: data?.address || "",
        whatsapp_number: data?.whatsapp_number || "",
        website: data?.website || "",
        email: data?.email || "",
        business_location_map: data?.business_location_map || "",
        latitude: data?.latitude || null,
        longitude: data?.longitude || null,
        pincode: data?.pincode?.toString() || "",
        contact_numbers: Array.isArray(data?.contact_numbers) ? data.contact_numbers : [""],
        payment_modes: filterValidPaymentModes(data?.payment_modes),
        business_timings: initializeTimings(data),
        services: getSelectedServices(data),
      });
    }
  }, [data, reset, paymentModes]);

  const {
    fields: contactNumbers,
    append: appendContactNumber,
    remove: removeContactNumber,
  } = useFieldArray({
    name: "contact_numbers",
    control,
  });

  useEffect(() => {
    if (contactNumbers.length === 0) {
      appendContactNumber("");
    }
  }, [contactNumbers.length, appendContactNumber]);

  const getGoogleMapsUrl = (lat, lng) => {
    return `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const mapUrl = getGoogleMapsUrl(latitude, longitude);
        setValue("business_location_map", mapUrl);
        setValue("latitude", latitude);
        setValue("longitude", longitude);
      },
      (error) => {
        console.error("Error getting location:", error);
        alert("Unable to retrieve your location. Please check your browser permissions.");
      }
    );
  };

  const onSubmit = (formData) => {
    console.log('BusinessOwnerEditForm - Raw form data:', formData);
    
    const cleanedData = {};
    
    if (formData.business_name && formData.business_name.trim() !== '') {
      cleanedData.business_name = formData.business_name;
    }
    
    if (formData.about_business !== undefined) {
      cleanedData.about_business = formData.about_business;
    }
    
    if (formData.address && formData.address.trim() !== '') {
      cleanedData.address = formData.address;
    }
    
    if (formData.whatsapp_number && formData.whatsapp_number.trim() !== '') {
      cleanedData.whatsapp_number = formData.whatsapp_number;
    }
    
    if (formData.website && formData.website.trim() !== '') {
      cleanedData.website = formData.website;
    }
    
    if (formData.email && formData.email.trim() !== '') {
      cleanedData.email = formData.email;
    }
    
    if (formData.business_location_map && formData.business_location_map.trim() !== '') {
      cleanedData.business_location_map = formData.business_location_map;
    }
    
    if (typeof formData.latitude === 'number' && !isNaN(formData.latitude)) {
      cleanedData.latitude = formData.latitude;
    }
    
    if (typeof formData.longitude === 'number' && !isNaN(formData.longitude)) {
      cleanedData.longitude = formData.longitude;
    }
    
    if (formData.pincode && formData.pincode.trim() !== '') {
      cleanedData.pincode = formData.pincode;
    }
    
    if (formData.contact_numbers && formData.contact_numbers.length > 0) {
      const filteredNumbers = formData.contact_numbers.filter(num => num && num.trim() !== '');
      if (filteredNumbers.length > 0) {
        cleanedData.contact_numbers = filteredNumbers;
      }
    }
    
    // Add payment modes if selected (same as admin)
    if (formData.payment_modes && formData.payment_modes.length > 0) {
      cleanedData.payment_modes = formData.payment_modes;
    }
    
    // Format business timings
    const formattedTimings = {};
    allDays.forEach((day) => {
      const daySlots = formData.business_timings[day] || [];
      const validSlots = daySlots.filter(slot => slot.open && slot.close);
      
      if (validSlots.length > 0) {
        formattedTimings[day] = validSlots;
      } else {
        formattedTimings[day] = [];
      }
    });
    
    cleanedData.business_timings = formattedTimings;
    
    // Add services if selected
    if (formData.services && formData.services.length > 0) {
      cleanedData.services = formData.services;
    }
    
    console.log('BusinessOwnerEditForm - Cleaned data:', cleanedData);
    onSave(cleanedData);
  };

  const activePaymentModes = paymentModes.filter(pm => pm.status);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Edit Business Details</h3>
        <p className="text-sm text-gray-500 mt-1">Update your business information</p>
      </div>
      
      {/* Basic Information */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-800">Basic Information</h4>
        
        <TextField
          label="Business Name"
          placeholder="Enter business name"
          {...register("business_name")}
          error={errors?.business_name?.message}
        />

        <TextAreaField
          label="About Business"
          rows={3}
          placeholder="Describe your business..."
          {...register("about_business")}
          error={errors?.about_business?.message}
        />
      </div>

      {/* Contact Information */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-800">Contact Information</h4>
        
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Contact Numbers
            </label>
            <button
              type="button"
              onClick={() => appendContactNumber("")}
              className="text-blue-600 text-sm hover:text-blue-800"
            >
              + Add Number
            </button>
          </div>
          <div className="space-y-2">
            {contactNumbers.map((field, index) => (
              <div key={field.id} className="flex gap-2">
                <TextField
                  placeholder="10-digit number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={10}
                  {...register(`contact_numbers.${index}`)}
                  error={errors?.contact_numbers?.[index]?.message}
                />
                {contactNumbers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeContactNumber(index)}
                    className="text-red-600 text-sm hover:text-red-800 px-3 py-2 border border-red-300 rounded"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField
            label="WhatsApp Number"
            placeholder="Enter WhatsApp number"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={15}
            {...register("whatsapp_number")}
            error={errors?.whatsapp_number?.message}
          />

          <TextField
            label="Email"
            placeholder="example@domain.com"
            {...register("email")}
            error={errors?.email?.message}
          />

          <TextField
            label="Website"
            placeholder="https://example.com"
            {...register("website")}
            error={errors?.website?.message}
          />
        </div>
      </div>

      {/* Location Information */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-800">Location Information</h4>
        
        <TextAreaField
          label="Address"
          rows={3}
          placeholder="Enter full address"
          {...register("address")}
          error={errors?.address?.message}
        />

        <TextField
          label="Pincode"
          placeholder="6-digit pincode"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          {...register("pincode")}
          error={errors?.pincode?.message}
        />

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Business Location Map URL
            </label>
            <button
              type="button"
              onClick={handleGetLocation}
              className="text-blue-600 text-sm hover:text-blue-800 flex items-center gap-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                />
              </svg>
              Get Current Location
            </button>
          </div>
          <TextField
            placeholder="https://maps.google.com/..."
            {...register("business_location_map")}
            error={errors?.business_location_map?.message}
          />
        </div>

        <input type="hidden" {...register("latitude", {
          setValueAs: (v) => v === "" || v === null || v === undefined ? null : parseFloat(v)
        })} />
        <input type="hidden" {...register("longitude", {
          setValueAs: (v) => v === "" || v === null || v === undefined ? null : parseFloat(v)
        })} />
      </div>

      {/* Payment Modes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payment Modes
        </label>
        <Controller
          control={control}
          name="payment_modes"
          defaultValue={[]}
          render={({ field }) => (
            <CheckboxGroupField
              value={field.value}
              onChange={field.onChange}
              options={activePaymentModes.map(pm => ({ label: pm.name, value: pm.name }))}
              error={errors?.payment_modes?.message}
            />
          )}
        />
      </div>

      {/* Services */}
      <div>
        <Controller
          control={control}
          name="services"
          defaultValue={[]}
          render={({ field }) => (
            <ServicesCheckboxGroup
              label="Services"
              value={field.value}
              onChange={field.onChange}
              options={services.map(service => ({ 
                label: service.name, 
                value: service.name 
              }))}
              loading={servicesLoading}
              error={errors?.services?.message}
              hint={categorySlug ? `Services for ${categorySlug} category. Check existing services or add custom ones.` : "Check existing services or add custom services"}
            />
          )}
        />
      </div>

      {/* Business Timings */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Business Timings (Shift-wise)
        </label>
        <div className="text-xs text-gray-500 mb-3">
          Configure up to 2 shifts per day. Leave empty if closed.
        </div>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {allDays.map((day) => (
            <div key={day} className="border rounded-lg p-3 bg-gray-50">
              <div className="text-sm font-semibold text-gray-700 mb-2 capitalize">
                {day}
              </div>
              
              {/* Slot 1 */}
              <div className="mb-2">
                <div className="text-xs font-medium text-gray-600 mb-1">Slot 1</div>
                <div className="grid grid-cols-2 gap-2">
                  <Controller
                    control={control}
                    name={`business_timings.${day}.0.open`}
                    render={({ field }) => (
                      <TextField
                        type="time"
                        placeholder="Open"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || "")}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`business_timings.${day}.0.close`}
                    render={({ field }) => (
                      <TextField
                        type="time"
                        placeholder="Close"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || "")}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Slot 2 */}
              <div>
                <div className="text-xs font-medium text-gray-600 mb-1">Slot 2</div>
                <div className="grid grid-cols-2 gap-2">
                  <Controller
                    control={control}
                    name={`business_timings.${day}.1.open`}
                    render={({ field }) => (
                      <TextField
                        type="time"
                        placeholder="Open"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || "")}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`business_timings.${day}.1.close`}
                    render={({ field }) => (
                      <TextField
                        type="time"
                        placeholder="Close"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || "")}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

// Discount Edit Form (reuse from BusinessEditModals)
export const BusinessOwnerDiscountEditForm = ({ data, onSave, loading = false }) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(discountSchema),
    defaultValues: {
      discount_text: data?.active_discount?.discount_text || "",
      fixed_discount: data?.active_discount?.fixed_discount || "",
      customer_discount: data?.active_discount?.customer_discount || "",
      company_commission: data?.active_discount?.company_commission || "",
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        discount_text: data?.active_discount?.discount_text || "",
        fixed_discount: data?.active_discount?.fixed_discount || "",
        customer_discount: data?.active_discount?.customer_discount || "",
        company_commission: data?.active_discount?.company_commission || "",
      });
    }
  }, [data, reset]);

  // COMMENTED OUT - Auto-calculation of fixed discount
  // const customerDiscount = watch("customer_discount");
  // const companyCommission = watch("company_commission");

  // useEffect(() => {
  //   const customer = parseFloat(customerDiscount) || 0;
  //   const company = parseFloat(companyCommission) || 0;
  //   const total = customer + company;
  //   setValue("fixed_discount", total > 0 ? total : "");
  // }, [customerDiscount, companyCommission, setValue]);

  const onSubmit = (formData) => {
    const cleanedData = {};
    
    // Only discount_text is needed
    if (formData.discount_text && formData.discount_text.trim() !== '') {
      cleanedData.discount_text = formData.discount_text;
    }
    
    // COMMENTED OUT - All percentage fields
    // if (formData.fixed_discount !== undefined && formData.fixed_discount !== null && formData.fixed_discount !== '') {
    //   cleanedData.fixed_discount = formData.fixed_discount;
    // }
    
    // if (formData.customer_discount !== undefined && formData.customer_discount !== null && formData.customer_discount !== '') {
    //   cleanedData.customer_discount = formData.customer_discount;
    // }
    
    // if (formData.company_commission !== undefined && formData.company_commission !== null && formData.company_commission !== '') {
    //   cleanedData.company_commission = formData.company_commission;
    // }
    
    onSave(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Edit Discount Information</h3>
        <p className="text-sm text-gray-500 mt-1">Update discount offer text displayed to customers</p>
      </div>
      
      <div>
        <TextField
          label="Discount Text"
          placeholder="e.g., Flat 10% off on all items"
          maxLength={32}
          {...register("discount_text")}
          error={errors?.discount_text?.message}
        />
      </div>

      {/* COMMENTED OUT - All discount percentage fields
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField
          label="Discount Percentage (%)"
          type="number"
          min={0}
          step="0.01"
          placeholder="0"
          {...register("customer_discount", {
            setValueAs: (v) => (v === "" ? null : parseFloat(v)),
          })}
          error={errors?.customer_discount?.message}
        />

        <TextField
          label="Company Commission (%)"
          type="number"
          min={0}
          step="0.01"
          placeholder="0"
          {...register("company_commission", {
            setValueAs: (v) => (v === "" ? null : parseFloat(v)),
          })}
          error={errors?.company_commission?.message}
        />

        <TextField
          label="Total Discount (%)"
          type="number"
          min={0}
          step="0.01"
          placeholder="0"
          disabled
          readOnly
          {...register("fixed_discount", {
            setValueAs: (v) => (v === "" ? null : parseFloat(v)),
          })}
          error={errors?.fixed_discount?.message}
        />
      </div>
      <p className="text-xs text-gray-500 mt-1">
        * Total Discount shows the discount percentage offered to customers
      </p>
      */}

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

// Gallery Edit Form (reuse from BusinessEditModals)
export const BusinessOwnerGalleryEditForm = ({ data, onSave, loading = false }) => {
  const [existingImages, setExistingImages] = useState(
    data?.gallery_images || []
  );
  const [newImages, setNewImages] = useState([]);
  const [imagesToRemove, setImagesToRemove] = useState([]);

  const handleRemoveExistingImage = (imageUuid) => {
    setExistingImages((prev) => prev.filter((img) => img.uuid !== imageUuid));
    setImagesToRemove((prev) => [...prev, imageUuid]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const cleanedData = {
      gallery: newImages || [],
      remove_images: imagesToRemove || [],
    };
    
    onSave(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Edit Gallery Images</h3>
        <p className="text-sm text-gray-500 mt-1">Manage business gallery photos (max 5 images)</p>
      </div>
      
      {existingImages.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Images
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {existingImages.map((img) => (
              <div key={img.uuid} className="relative group">
                <div className="aspect-square">
                  <img
                    src={img.image}
                    alt="Gallery"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveExistingImage(img.uuid)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {existingImages.length < 5 && (
        <div>
          <ImageDropField
            label={existingImages.length > 0 ? "Add More Images" : "Add New Images"}
            value={newImages}
            onChange={setNewImages}
            hint={`Drop up to ${5 - existingImages.length} images or click to browse`}
            maxFiles={5 - existingImages.length}
          />
        </div>
      )}

      {existingImages.length >= 5 && (
        <div className="text-center py-4 text-gray-500">
          <p>Maximum of 5 images allowed. Remove some existing images to add new ones.</p>
        </div>
      )}

      {existingImages.length === 0 && (!newImages || newImages.length === 0) && (
        <div className="text-center py-8 text-gray-500">
          <p>No gallery images found. Add some images to showcase your business.</p>
        </div>
      )}

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};
